import { useEffect, useState } from "react";
import { expandAccordion } from "../util/scrollToElement";

export default function usePrint(
  tocForPrint,
  checklist,
  tabType,
  activeSection,
  targetId,
  isDetailsTab
) {
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedReferences, setSelectedReferences] = useState([]);
  const [tocReferences, setTocReferences] = useState([]);

  const [refHash, setRefHash] = useState({});
  const [selectedSections, setSelectedSections] = useState([]);

  const detailsTocForPrint = [];

  tocForPrint?.forEach((item) => {
    if (!item?.reference) {
      const tempTocItem = { ...item, reference: item.title };
      detailsTocForPrint.push(tempTocItem);
    } else {
      detailsTocForPrint.push(item);
    }
  });

  const buildReferences = (localToc) => {
    const tempRefHash = [];
    for (const tocItem of localToc) {
      tempRefHash[tocItem.reference] = [tocItem.reference];
      tempRefHash[tocItem.reference] = buildSubReferences(
        tocItem.subSection,
        tempRefHash[tocItem.reference]
      );
    }
    return tempRefHash;
  };
  const buildSubReferences = (subSection, references) => {
    if (!subSection) {
      return references;
    }
    for (const subSectionItem of subSection) {
      references.push(subSectionItem.reference);
      references = buildSubReferences(subSectionItem.subSection, references);
    }
    return references;
  };
  const getParentReference = (toc, childRef) => {
    let parentRef;
    toc.forEach((section) => {
      if (section.subSection) {
        const matched = section.subSection.filter((subsect) => {
          return subsect.reference === childRef;
        });
        parentRef = matched.length ? section.reference : parentRef; // if more than one section w subs, parentRef may already be set
      }
    });
    const parentSection = toc.find((item) => item.reference === parentRef);
    return { parentSection, parentRef };
  };

  useEffect(() => {
    selectedReferences.forEach((section) => {
      expandAccordion(document.getElementById(section));
    });
  }, [selectedReferences]);

  useEffect(() => {
    deselectAll();
  }, []);

  useEffect(
    () => {
      const allPrintReferences = [];
      const getAllPrintReferences = (item) => {
        allPrintReferences.push(item.reference);
        if (item.subSection && item.subSection.length) {
          item.subSection.forEach((subItem) => getAllPrintReferences(subItem));
        }
      };
      if (tabType === undefined && tocForPrint?.length > 0) {
        tocForPrint.forEach((item) => getAllPrintReferences(item));
        setTocReferences(allPrintReferences);
        setRefHash(buildReferences(tocForPrint));
      } else if (isDetailsTab && detailsTocForPrint?.length > 0) {
        detailsTocForPrint.forEach((item) => getAllPrintReferences(item));
        setTocReferences(allPrintReferences);
        setRefHash(buildReferences(detailsTocForPrint));
      } else if (tabType === "checklist" && checklist && checklist.length > 0) {
        const checkListPrintReferences = checklist.map((item) => item.id);
        const tempRefHash = [];
        for (const ref of checkListPrintReferences) {
          tempRefHash[ref] = [ref];
        }
        setRefHash(tempRefHash);
        setTocReferences(checkListPrintReferences);
      } else {
        //There is nothing to do here so Sonar be happy please
      }
    },
    // eslint-disable-next-line
    [tocForPrint, checklist, tabType]
  );

  const handleInitialPrintDialogSelections = () => {
    const target = targetId?.length
      ? targetId
      : activeSection?.id || activeSection?.sectionId;
    const isParent = !!refHash[target];
    const targetObject = {
      target: { value: target },
    };
    isParent
      ? handlePrintDialogParentOptionChange(targetObject)
      : handlePrintDialogChildOptionChange(targetObject);
  };

  const handlePrintDialogParentOptionChange = (e) => {
    const parentRef = e.target.value;
    const hasParentRefAlreadySelected =
      selectedReferences.includes(parentRef);

    if (hasParentRefAlreadySelected) {
      const newSelectedSections = selectedSections.filter(
        (selectedSection) => selectedSection !== parentRef
      );
      setSelectedSections([...newSelectedSections]);
      const newSelectedReferences = selectedReferences.filter(
        (selectedReference) => !refHash[parentRef]?.includes(selectedReference)
      );
      setSelectedReferences([...newSelectedReferences]);
    } else {
      setSelectedSections([...selectedSections, parentRef]);
      setSelectedReferences([
        ...new Set([...selectedReferences, ...refHash[parentRef]]),
      ]);
    }
  };

  const recursivelyRemoveRef = (selectedRefs, section) => {
    const subSections = section?.subSection ?? [];
    selectedRefs = selectedRefs.filter((ref) => ref !== section.reference);
    for (const subSectionsItem of subSections) {
      selectedRefs = recursivelyRemoveRef(selectedRefs, subSectionsItem);
    }
    return selectedRefs;
  };

  const recursivelyAddRef = (selectedRefs, section) => {
    const subSections = section?.subSection ?? [];
    selectedRefs.push(section.reference);
    for (const subSectionsItem of subSections) {
      selectedRefs = recursivelyAddRef(selectedRefs, subSectionsItem);
    }
    return selectedRefs;
  };

  const handlePrintDialogChildOptionChange = (e) => {
    const childRef = e.target.value;
    const hasChildRefAlreadySelected =
      selectedReferences.includes(childRef);
    let parentRef;
    let parentSection;

    if (isDetailsTab) {
      ({ parentSection, parentRef } = getParentReference(
        detailsTocForPrint,
        childRef
      ));
    } else {
      ({ parentSection, parentRef } = getParentReference(
        tocForPrint,
        childRef
      ));
    }

    //if initial section is a deeply nested child, just select the parent section and it's children
    if (!parentSection) {
      handlePrintDialogParentOptionChange({
        target: { value: activeSection.id },
      });

      return;
    }

    const childSection = parentSection.subSection?.find(
      (item) => item.reference === childRef
    );
    if (hasChildRefAlreadySelected) {
      setSelectedReferences(
        recursivelyRemoveRef(
          selectedReferences.filter((ref) => ref !== parentRef),
          childSection
        )
      );
    } else {
      const newSelectedRefs = recursivelyAddRef([], childSection);
      const localSelectedReferences = [...selectedReferences, childRef];
      const subSectionRefs =
        parentSection.subSection?.map((item) => item.reference) ?? [];
      const haveAllSubRefsSelected = subSectionRefs.every(
        (item) => localSelectedReferences.includes(item)
      );
      if (haveAllSubRefsSelected) {
        setSelectedReferences([
          ...selectedReferences,
          parentRef,
          ...newSelectedRefs,
        ]);
      } else {
        setSelectedReferences([...selectedReferences, ...newSelectedRefs]);
      }
    }
  };

  const onPrint = () => {
    window.print();
  };

  const handlePrintDialogSelectAll = () => {
    setSelectedReferences(tocReferences);
    let allRefs = [];
    let allSections = [];
    for (const ref in refHash) {
      if (ref) {
        allRefs = [...allRefs, ...refHash[ref]];
        allSections = [...allSections, ref];
      }
    }
    setSelectedSections(allSections);
  };

  const deselectAll = () => {
    setSelectedReferences([]);
    setSelectedSections([]);
  };
  const handlePrintDialogDeselectAll = () => deselectAll();

  const handlePrintDialogCancel = () => {
    deselectAll();
    setIsPrintDialogOpen(false);
  };

  return {
    detailsTocForPrint,
    selectedSections,
    selectedReferences,
    setSelectedReferences,
    isPrintDialogOpen,
    setIsPrintDialogOpen,
    tocReferences,
    onPrint,
    handlePrintDialogParentOptionChange,
    handlePrintDialogChildOptionChange,
    handlePrintDialogSelectAll,
    handlePrintDialogDeselectAll,
    handlePrintDialogCancel,
    handleInitialPrintDialogSelections,
  };
}
